import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, User, Package, Heart, DollarSign } from "lucide-react";

const fmtUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const AdminCustomerDetail = () => {
  const { user_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/customers/${user_id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error("Failed to load", { description: e.response?.data?.detail || "" }))
      .finally(() => setLoading(false));
  }, [user_id]);

  if (loading) return <AdminLayout title="Customer"><div className="text-muted-foreground">Loading…</div></AdminLayout>;
  if (!data) return <AdminLayout title="Customer"><div className="text-muted-foreground">Not found.</div></AdminLayout>;

  const c = data.customer;
  const totalSpend = data.purchases.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <AdminLayout title={c.name} actions={
      <Link to="/admin/customers" data-testid="back-to-customers" className="text-sm text-muted-foreground hover:text-accent flex items-center gap-2"><ArrowLeft size={14} /> Back to customers</Link>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="border border-border bg-card p-6 space-y-4">
          <div className="overline text-accent"><User size={12} className="inline mr-1" /> Profile</div>
          <div>
            <div className="font-display text-2xl" data-testid="cust-name">{c.name}</div>
            <div className="text-sm text-muted-foreground" data-testid="cust-email">{c.email}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><div className="text-muted-foreground">Registered</div><div>{fmt(c.created_at)}</div></div>
            <div><div className="text-muted-foreground">Last login</div><div>{fmt(c.last_login_at)}</div></div>
            <div><div className="text-muted-foreground">Country</div><div>{c.country || "—"}</div></div>
            <div><div className="text-muted-foreground">Status</div><div>{c.is_active !== false ? "Active" : "Suspended"}</div></div>
            <div className="col-span-2"><div className="text-muted-foreground">Email verified</div><div>{c.email_verified ? "Yes ✓" : "No"}</div></div>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <div className="overline text-accent"><DollarSign size={12} className="inline mr-1" /> Spend</div>
          <div className="mt-4">
            <div className="font-display text-4xl" data-testid="cust-total-spend">{fmtUSD(totalSpend)}</div>
            <div className="text-xs text-muted-foreground mt-2">{data.purchases.length} purchase(s)</div>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <div className="overline text-accent"><Heart size={12} className="inline mr-1" /> Wishlist</div>
          <div className="mt-4">
            <div className="font-display text-4xl" data-testid="cust-wishlist-count">{data.wishlist.length}</div>
            <div className="text-xs text-muted-foreground mt-2">plans saved</div>
          </div>
        </div>
      </div>

      <div className="mt-6 border border-border bg-card">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <Package size={14} className="text-accent" />
          <div className="overline text-accent">Purchase history</div>
        </div>
        <div className="divide-y divide-border" data-testid="cust-purchases">
          {data.purchases.length === 0 && <div className="p-6 text-sm text-muted-foreground">No purchases yet.</div>}
          {data.purchases.map((p) => (
            <div key={p.purchase_id} className="p-4 grid grid-cols-1 md:grid-cols-[1fr_150px_120px_150px] gap-3 items-center">
              <div className="min-w-0">
                <div className="text-sm font-medium">Order {p.purchase_id.slice(-10)}</div>
                <div className="text-xs text-muted-foreground truncate">Plans: {(p.plan_ids || []).join(", ")}</div>
              </div>
              <div className="text-xs">{fmt(p.created_at)}</div>
              <div className="text-xs uppercase tracking-widest text-accent">{p.provider || "stripe"}</div>
              <div className="text-sm font-medium">{p.currency?.toUpperCase()} {p.amount}</div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCustomerDetail;
